<template>
  <div class="test-room">
    <AppBreadcrumb :items="breadcrumbItems" :showBack="true" @back="goBack">
      <template #actions>
        <div class="header-actions">
          <a-tag color="blue" class="mobile-tag">
            {{ currentIndex + 1 }} / {{ questions.length }}
          </a-tag>

          <a-tag color="green" class="mobile-tag">
            <span class="tag-icon">✅</span>
            {{ answeredCount }} / {{ questions.length }}
          </a-tag>

          <a-tag v-if="isWrongMode" color="orange" class="wrong-counter-tag">
            <span class="wrong-counter-icon">⚠️</span>
            {{ $t('testRoom.remainingWrong') }}: {{ remainingWrongCount }}
          </a-tag>

          <a-button
            type="primary"
            danger
            :loading="finishLoading"
            :disabled="isFinished"
            @click="finishTest"
            class="finish-btn"
          >
            <span class="btn-text">{{ $t('testRoom.finish') }}</span>
          </a-button>
        </div>
      </template>
    </AppBreadcrumb>

    <template v-if="questions.length">
      <div class="test-container">
        <div class="question-panel">
          <div class="panel-card">
            <div class="title-row">
              <div>
                <div class="test-title">{{ title }}</div>
                <div class="test-subtitle">
                  {{ $t('testRoom.question') }} {{ currentIndex + 1 }} / {{ questions.length }}
                </div>
              </div>
              <div v-if="isWrongMode" class="wrong-badge">
                <span class="wrong-badge-icon">⚠️</span>
                <span class="wrong-badge-text">{{ $t('testRoom.wrongQuestion') }}</span>
              </div>
              <div v-else class="mode-badge">
                <span class="mode-badge-icon">📝</span>
                <span class="mode-badge-text">{{ $t('testRoom.questionLabel') }}</span>
              </div>
            </div>

            <div class="question-content">
              <!-- Savol matni - tilga mos -->
              <h2 class="question-title">
                {{ getLocalizedText(currentQuestion, 'text') || $t('testRoom.questionNotFound') }}
              </h2>

              <!-- Oldingi xato javob -->
              <div v-if="isWrongMode && currentQuestion.previous_wrong_answer" class="wrong-info-card">
                <div class="wrong-info-header">
                  <span class="wrong-info-icon">⚠️</span>
                  <span class="wrong-info-title">{{ $t('testRoom.previousWrongAnswer') }}</span>
                </div>
                <div class="wrong-info-content">
                  {{ getLocalizedOptionText(currentQuestion.previous_wrong_answer) }}
                </div>
              </div>

              <!-- Variantlar -->
              <div class="options">
                <button
                  v-for="(option, index) in currentQuestion.options || []"
                  :key="option.id"
                  class="option-btn"
                  :class="getOptionClass(option)"
                  :disabled="finishLoading || isFinished"
                  @click="selectAnswer(option)"
                >
                  <span class="option-hotkey">F{{ index + 1 }}</span>
                  <span class="option-text">{{ getLocalizedOptionText(option) }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="info-panel">
          <div class="panel-card">
            <div class="image-wrap">
              <a-image
                v-if="currentQuestion.image_url"
                :src="currentQuestion.image_url"
                class="question-image"
                :alt="$t('testRoom.questionImage')"
              />
              <div v-else class="no-image">
                <img src="../../public/no-image.png" :alt="$t('testRoom.noImage')" />
              </div>
            </div>

            <div v-if="showExplanation" class="explanation">
              <div class="explanation-title">
                <span class="explanation-icon">💡</span>
                {{ $t('testRoom.explanation') }}
              </div>
              <div class="explanation-text">
                {{ getLocalizedExplanation(currentQuestion) || $t('testRoom.noExplanation') }}
              </div>

              <div v-if="isCurrentAnswerCorrect" class="result-alert success">
                <span class="alert-icon">✅</span>
                <span class="alert-text">{{ $t('testRoom.correctAnswer') }}</span>
              </div>

              <div v-else-if="isCurrentAnswerWrong" class="result-alert error">
                <span class="alert-icon">❌</span>
                <span class="alert-text">{{ $t('testRoom.wrongAnswer') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="bottom-nav">
        <div class="question-number-grid">
          <button
            v-for="(q, index) in questions"
            :key="q.id"
            class="question-nav-btn"
            :class="getQuestionBtnClass(index)"
            @click="goToQuestion(index)"
          >
            {{ index + 1 }}
          </button>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="empty-state">
        <a-empty :description="$t('testRoom.noQuestions')" />
      </div>
    </template>
  </div>
</template>

<script>
import api, { MEDIA_BASE_URL } from '@/api/axios'
import AppBreadcrumb from './AppBreadcrumb.vue'

export default {
  name: 'TicketTestRoom',

  components: {
    AppBreadcrumb
  },

  props: {
    title: { type: String, default: '' },
    questions: { type: Array, default: () => [] },
    resultId: { type: Number, required: true },
    backRoute: { type: String, default: '/' },
    pageTitle: { type: String, default: 'Biletlar' },
    ticketType: { type: Number, default: null },
    ticketNumber: { type: Number, default: null },
    mode: { type: String, default: 'ticket' },
    wrongQuestionIds: { type: Array, default: () => [] }
  },

  emits: ['back', 'go-list', 'finished'],

  data() {
    return {
      currentIndex: 0,
      selectedAnswers: {},
      showExplanation: false,
      finishLoading: false,
      isFinished: false,
      errorMessage: '',
      startedAt: Date.now(),
      resolvedWrongQuestions: [],
      isMobile: window.innerWidth <= 768
    }
  },

  computed: {
    currentLanguage() {
      return this.$i18n.locale || 'uz'
    },

    isWrongMode() {
      return this.mode === 'wrong'
    },

    currentQuestion() {
      return this.questions?.[this.currentIndex] || {}
    },

    selectedOptionId() {
      return this.selectedAnswers[this.currentQuestion?.id] || null
    },

    answeredCount() {
      return Object.keys(this.selectedAnswers).length
    },

    allAnswered() {
      return this.questions.length > 0 && this.answeredCount === this.questions.length
    },

    breadcrumbItems() {
      return [
        { label: this.pageTitle, click: this.goToList },
        { label: this.title }
      ]
    },

    storageKey() {
      if (this.isWrongMode) {
        return `wrong_session_${this.resultId}`
      }
      return `ticket_session_${this.ticketType}_${this.ticketNumber}`
    },

    isCurrentAnswerCorrect() {
      if (!this.currentQuestion?.id) return false
      return this.isQuestionCorrectById(this.currentQuestion.id)
    },

    isCurrentAnswerWrong() {
      if (!this.currentQuestion?.id) return false
      return this.isQuestionWrongById(this.currentQuestion.id)
    },

    remainingWrongCount() {
      return this.wrongQuestionIds.length - this.resolvedWrongQuestions.length
    }
  },

  mounted() {
    this.restoreSession()
    this.restoreCurrentQuestionState()
    window.addEventListener('keydown', this.handleKeydown)
    window.addEventListener('resize', this.handleResize)
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown)
    window.removeEventListener('resize', this.handleResize)
    if (!this.isFinished) {
      this.saveSession()
    }
  },

  watch: {
    currentIndex() {
      this.saveSession()
      this.restoreCurrentQuestionState()
    },
    selectedAnswers: {
      deep: true,
      handler() {
        this.saveSession()
      }
    }
  },

  methods: {
    // ============ TILGA MOS MATN OLISH FUNKSIYALARI ============
    
    /**
     * Joriy tilni olish
     */
    getCurrentLanguage() {
      return this.currentLanguage === 'uz_cyrl' ? 'uz_cyrl' : 'uz'
    },

    /**
     * Savol matnini tilga mos qaytarish
     */
    getLocalizedText(question, field = 'text') {
      if (!question) return ''
      const lang = this.getCurrentLanguage()
      if (lang === 'uz_cyrl' && question[`${field}_cyrillic`]) {
        return question[`${field}_cyrillic`]
      }
      return question[field] || ''
    },

    /**
     * Variant matnini tilga mos qaytarish
     */
    getLocalizedOptionText(option) {
      if (!option) return ''
      const lang = this.getCurrentLanguage()
      if (lang === 'uz_cyrl' && option.text_cyrillic) {
        return option.text_cyrillic
      }
      return option.text || ''
    },

    /**
     * Izoh matnini tilga mos qaytarish
     */
    getLocalizedExplanation(question) {
      if (!question) return ''
      const lang = this.getCurrentLanguage()
      if (lang === 'uz_cyrl' && question.explanation_cyrillic) {
        return question.explanation_cyrillic
      }
      return question.explanation || ''
    },

    

    // ============ QOLGAN METODLAR ============

    handleResize() {
      this.isMobile = window.innerWidth <= 768
    },

    getAuthHeaders() {
      const token = localStorage.getItem('access_token') || localStorage.getItem('accessToken')
      const headers = { 
        'Content-Type': 'application/json',
        'X-Locale': this.currentLanguage
      }
      if (token) headers.Authorization = `Bearer ${token}`
      return headers
    },

    saveSession() {
      const payload = {
        progress: {
          currentIndex: this.currentIndex,
          selectedAnswers: this.selectedAnswers,
          showExplanation: this.showExplanation,
          isFinished: this.isFinished,
          startedAt: this.startedAt,
          resolvedWrongQuestions: this.resolvedWrongQuestions
        }
      }

      if (!this.isWrongMode) {
        payload.selectedTicket = {
          title: this.title,
          ticketNumber: this.ticketNumber,
          resultId: this.resultId,
          questions: this.questions
        }
      }

      localStorage.setItem(this.storageKey, JSON.stringify(payload))
    },

    restoreSession() {
      try {
        const raw = localStorage.getItem(this.storageKey)
        if (!raw) return

        const parsed = JSON.parse(raw)
        const progress = parsed?.progress || {}

        if (typeof progress.currentIndex === 'number') {
          this.currentIndex = Math.min(
            Math.max(progress.currentIndex, 0),
            Math.max(this.questions.length - 1, 0)
          )
        }

        this.selectedAnswers = progress.selectedAnswers || {}
        this.showExplanation = !!progress.showExplanation
        this.isFinished = !!progress.isFinished
        this.startedAt = progress.startedAt || Date.now()
        this.resolvedWrongQuestions = progress.resolvedWrongQuestions || []
      } catch (error) {
        console.error('Session restore xatosi:', error)
      }
    },

    clearSession() {
      localStorage.removeItem(this.storageKey)
    },

    goBack() {
      this.clearSession()
      this.$emit('back')
    },

    goToList() {
      this.clearSession()
      this.$emit('go-list')
    },

    handleKeydown(e) {
      const map = { F1: 0, F2: 1, F3: 2, F4: 3, F5: 4, F6: 5 }

      if (map[e.key] !== undefined && !this.finishLoading && !this.isFinished) {
        e.preventDefault()
        const option = this.currentQuestion.options?.[map[e.key]]
        if (option) {
          this.selectAnswer(option)
        }
      }
    },

    getCorrectOptionId(question) {
      if (!question?.options?.length) return null
      const correctOption = question.options.find(option => option.is_correct === true)
      return correctOption ? correctOption.id : null
    },

    isQuestionAnswered(questionId) {
      return !!this.selectedAnswers[questionId]
    },

    isQuestionCorrectById(questionId) {
      const question = this.questions.find(q => q.id === questionId)
      if (!question) return false

      const selectedId = this.selectedAnswers[questionId]
      const correctId = this.getCorrectOptionId(question)

      if (!selectedId || !correctId) return false
      return Number(selectedId) === Number(correctId)
    },

    isQuestionWrongById(questionId) {
      const question = this.questions.find(q => q.id === questionId)
      if (!question) return false

      const selectedId = this.selectedAnswers[questionId]
      const correctId = this.getCorrectOptionId(question)

      if (!selectedId || !correctId) return false
      return Number(selectedId) !== Number(correctId)
    },

    selectAnswer(option) {
      if (this.finishLoading || this.isFinished || !this.currentQuestion?.id || this.isQuestionAnswered(this.currentQuestion.id)) return;

      this.errorMessage = '';
      this.selectedAnswers = {
        ...this.selectedAnswers,
        [this.currentQuestion.id]: option.id
      };
      this.showExplanation = true;
      this.saveSession();

      setTimeout(() => {
        if (this.isFinished) return;

        if (this.isWrongMode && this.isQuestionCorrectById(this.currentQuestion.id)) {
          const wrongQuestionId = this.currentQuestion.wrong_question_id
          if (wrongQuestionId) {
            this.resolvedWrongQuestions.push(wrongQuestionId)
          }
        }

        if (this.allAnswered) {
          this.finishTest();
          return;
        }

        if (!this.isWrongMode && this.isQuestionWrongById(this.currentQuestion.id)) {
          this.errorMessage = this.$t('testRoom.selectCorrect');
          return;
        }

        if (this.currentIndex < this.questions.length - 1) {
          this.currentIndex += 1;
          this.restoreCurrentQuestionState();
          this.saveSession();
        }
      }, 350);
    },

    restoreCurrentQuestionState() {
      this.showExplanation = !!this.selectedAnswers[this.currentQuestion?.id]
    },

    getOptionClass(option) {
      const classes = [];

      const selectedId = this.selectedOptionId
      const correctId = this.getCorrectOptionId(this.currentQuestion)

      if (!selectedId) return classes;

      if (Number(option.id) === Number(selectedId)) {
        classes.push('is-selected');

        if (Number(selectedId) === Number(correctId)) {
          classes.push('is-correct');
        } else {
          classes.push('is-wrong');
        }
      }

      if (
        correctId &&
        Number(selectedId) !== Number(correctId) &&
        Number(option.id) === Number(correctId)
      ) {
        classes.push('is-correct');
      }

      return classes;
    },

    buildAnswersPayload() {
      return this.questions.map((question) => ({
        question_id: question.id,
        selected_option_id: this.selectedAnswers[question.id] || null
      }))
    },

    async finishTest() {
      if (this.isFinished || this.finishLoading) return

      this.finishLoading = true
      this.errorMessage = ''

      try {
        const durationSeconds = Math.floor((Date.now() - this.startedAt) / 1000)

        if (this.isWrongMode && this.resolvedWrongQuestions.length > 0) {
          await Promise.all(
            this.resolvedWrongQuestions.map(wrongId =>
              api.post(
                `/v1/tests/wrong-question-resolve/${wrongId}/`,
                { selected_option_id: this.selectedAnswers[this.getQuestionIdByWrongId(wrongId)] }
              ).catch(err => console.log('Xato savol o\'chirishda xatolik:', err))
            )
          )
        }

        const response = await api.post(
          `/v1/tests/finish/`,
          {
            result_id: this.resultId,
            duration_seconds: durationSeconds,
            answers: this.buildAnswersPayload()
          }
        )

        console.log(response.data.message)

        this.isFinished = true
        this.clearSession()
        this.$emit('finished')

        this.$router.push({
          path: '/result',
          query: { result_id: this.resultId }
        })
      } catch (error) {
        console.error('finish-test xatosi:', error)
        this.errorMessage =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          (this.$t('examRoom.finishError'))
      } finally {
        this.finishLoading = false
      }
    },

    getQuestionIdByWrongId(wrongId) {
      const question = this.questions.find(q => q.wrong_question_id === wrongId)
      return question?.id
    },

    goToQuestion(index) {
      if (index < 0 || index >= this.questions.length) return
      this.currentIndex = index
      this.restoreCurrentQuestionState()
      this.saveSession()
    },

    getQuestionBtnClass(index) {
      const classes = []
      const question = this.questions[index]

      if (this.currentIndex === index) {
        classes.push('active')
      }

      if (question) {
        if (this.isQuestionCorrectById(question.id)) {
          classes.push('answered-correct')
        } else if (this.isQuestionWrongById(question.id)) {
          classes.push('answered-wrong')
        }
      }

      return classes
    }
  }
}
</script>



<style scoped>
.test-room {
  padding-bottom: 100px;
  background: var(--bg);
  min-height: 100vh;
}

.test-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.question-panel,
.info-panel {
  min-width: 0;
}

.panel-card {
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: 20px;
  padding: 24px;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.test-title {
  font-size: 18px;
  font-weight: 800;
  color: var(--text);
}

.test-subtitle {
  font-size: 13px;
  color: var(--text-soft);
  margin-top: 4px;
}

.wrong-badge {
  background: linear-gradient(135deg, #ff4d4f, #ff7875);
  padding: 6px 14px;
  border-radius: 30px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 2px 8px rgba(255, 77, 79, 0.3);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 2px 8px rgba(255, 77, 79, 0.3);
  }
  50% {
    transform: scale(1.02);
    box-shadow: 0 4px 16px rgba(255, 77, 79, 0.5);
  }
}

.wrong-badge-icon {
  font-size: 16px;
}

.wrong-badge-text {
  font-size: 12px;
  font-weight: 800;
  color: white;
  letter-spacing: 0.5px;
}

.mode-badge {
  background: rgba(59, 130, 246, 0.15);
  padding: 6px 14px;
  border-radius: 30px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

.mode-badge-icon {
  font-size: 14px;
}

.mode-badge-text {
  font-size: 12px;
  font-weight: 600;
  color: var(--primary);
}

.question-content {
  flex: 1;
}

.question-title {
  font-size: 22px;
  line-height: 1.4;
  margin: 0 0 24px;
  color: var(--text);
  font-weight: 700;
  word-wrap: break-word;
}

.wrong-info-card {
  background: rgba(255, 77, 79, 0.1);
  border-left: 4px solid #ff4d4f;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.wrong-info-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.wrong-info-icon {
  font-size: 16px;
}

.wrong-info-title {
  font-size: 13px;
  font-weight: 700;
  color: #ff4d4f;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.wrong-info-content {
  font-size: 14px;
  color: var(--text);
  padding-left: 24px;
  line-height: 1.5;
}

.options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.option-btn {
  min-height: 56px;
  height: auto;
  border-radius: 14px;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  background: var(--card-bg);
  border: 2px solid var(--border);
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  width: 100%;
}

.option-btn:hover {
  transform: translateX(4px);
  border-color: var(--primary);
}

.option-btn.is-selected {
  border-width: 2px;
}

.option-btn.is-correct {
  background: linear-gradient(135deg, #52c41a, #73d13d);
  border-color: #52c41a;
  color: white;
}

.option-btn.is-wrong {
  background: linear-gradient(135deg, #ff4d4f, #ff7875);
  border-color: #ff4d4f;
  color: white;
}

.option-hotkey {
  min-width: 44px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 800;
  background: rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(4px);
}

.option-text {
  flex: 1;
  white-space: normal;
  line-height: 1.4;
  font-size: 14px;
}

.image-wrap {
  width: 100%;
  min-height: 260px;
  border-radius: 16px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.02);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.question-image {
  width: 100%;
  max-height: 320px;
  object-fit: contain;
  display: block;
}

.no-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.explanation {
  padding-top: 16px;
  border-top: 1px solid var(--border);
}

.explanation-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 12px;
  color: var(--text);
}

.explanation-icon {
  font-size: 16px;
}

.explanation-text {
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-soft);
  margin-bottom: 16px;
}

.result-alert {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
}

.result-alert.success {
  background: rgba(82, 196, 26, 0.1);
  border: 1px solid rgba(82, 196, 26, 0.3);
  color: #52c41a;
}

.result-alert.error {
  background: rgba(255, 77, 79, 0.1);
  border: 1px solid rgba(255, 77, 79, 0.3);
  color: #ff4d4f;
}

.alert-icon {
  font-size: 18px;
}

.alert-text {
  flex: 1;
}

.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--card-bg);
  border-top: 1px solid var(--border);
  padding: 12px 16px;
  z-index: 100;
  backdrop-filter: blur(10px);
}

.question-number-grid {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  margin: 0 auto;
  overflow-x: auto;
  padding: 4px 0;
}

.question-nav-btn {
  min-width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: var(--card-bg);
  color: var(--text);
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.question-nav-btn:hover {
  background: rgba(59, 130, 246, 0.1);
  transform: scale(1.05);
}

.question-nav-btn.active {
  background: var(--primary);
  color: white;
  border-color: var(--primary);
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
}

.question-nav-btn.answered-correct {
  background: #52c41a;
  color: white;
  border-color: #52c41a;
}

.question-nav-btn.answered-wrong {
  background: #ff4d4f;
  color: white;
  border-color: #ff4d4f;
  animation: wrongPulse 0.5s ease;
}

@keyframes wrongPulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.question-number-grid::-webkit-scrollbar {
  height: 4px;
}

.question-number-grid::-webkit-scrollbar-track {
  background: var(--border);
  border-radius: 4px;
}

.question-number-grid::-webkit-scrollbar-thumb {
  background: var(--primary);
  border-radius: 4px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.mobile-tag {
  font-size: 13px;
  padding: 4px 10px;
}

.tag-icon {
  margin-right: 4px;
}

.wrong-counter-tag {
  font-size: 13px;
  font-weight: 700;
  padding: 4px 12px;
  background: #ff4d4f !important;
  color: white !important;
  animation: tagPulse 1s infinite;
}

@keyframes tagPulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

.wrong-counter-icon {
  margin-right: 4px;
}

.finish-btn {
  font-weight: 600;
}

.empty-state {
  padding: 48px 0;
  text-align: center;
}

/* Tablet (768px - 1024px) */
@media (max-width: 1024px) {
  .test-container {
    padding: 0 20px;
    gap: 20px;
  }
  
  .panel-card {
    padding: 20px;
  }
  
  .question-title {
    font-size: 20px;
  }
}

/* Mobile (576px - 768px) */
@media (max-width: 768px) {
  .test-container {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 0 16px;
  }
  
  .panel-card {
    padding: 16px;
  }
  
  .title-row {
    margin-bottom: 16px;
  }
  
  .test-title {
    font-size: 16px;
  }
  
  .test-subtitle {
    font-size: 12px;
  }
  
  .question-title {
    font-size: 18px;
    margin-bottom: 20px;
  }
  
  .option-btn {
    min-height: 50px;
    padding: 10px 14px;
  }
  
  .option-hotkey {
    min-width: 38px;
    height: 32px;
    font-size: 12px;
  }
  
  .option-text {
    font-size: 13px;
  }
  
  .wrong-badge,
  .mode-badge {
    padding: 4px 10px;
  }
  
  .wrong-badge-text,
  .mode-badge-text {
    font-size: 10px;
  }
  
  .wrong-info-card {
    padding: 12px;
    margin-bottom: 20px;
  }
  
  .wrong-info-content {
    font-size: 13px;
  }
  
  .image-wrap {
    min-height: 200px;
  }
  
  .question-nav-btn {
    min-width: 36px;
    height: 36px;
    font-size: 13px;
  }
  
  .header-actions {
    gap: 8px;
  }
  
  .mobile-tag,
  .wrong-counter-tag {
    font-size: 11px;
    padding: 2px 8px;
  }
  
  .finish-btn .btn-text {
    display: inline;
  }
}

/* Small Mobile (320px - 576px) */
@media (max-width: 576px) {
  .test-container {
    padding: 0 12px;
    gap: 12px;
  }
  
  .panel-card {
    padding: 14px;
  }
  
  .test-title {
    font-size: 15px;
  }
  
  .question-title {
    font-size: 16px;
    margin-bottom: 16px;
  }
  
  .option-btn {
    min-height: 46px;
    gap: 10px;
    padding: 8px 12px;
  }
  
  .option-hotkey {
    min-width: 34px;
    height: 28px;
    font-size: 11px;
  }
  
  .option-text {
    font-size: 12px;
  }
  
  .wrong-info-content {
    font-size: 12px;
    padding-left: 20px;
  }
  
  .image-wrap {
    min-height: 160px;
  }
  
  .explanation-text {
    font-size: 13px;
  }
  
  .question-nav-btn {
    min-width: 32px;
    height: 32px;
    font-size: 12px;
  }
  
  .header-actions {
    gap: 6px;
  }
  
  .mobile-tag,
  .wrong-counter-tag {
    font-size: 10px;
    padding: 2px 6px;
  }
}

/* Extra small (320px dan kichik) */
@media (max-width: 380px) {
  .header-actions {
    flex-wrap: wrap;
  }
  
  .question-nav-btn {
    min-width: 28px;
    height: 28px;
    font-size: 11px;
  }
  
  .bottom-nav {
    padding: 8px 12px;
  }
}
</style>